<template>
    <DModal
        :show="showModal"
        :is-awaiting-validation="true"
        :title="sectionTitle"
        is-confirm
        is-medium
        @close="close"
        @confirm="apply"
    >
        <div class="modal-content px-4 mb-4">
            <CCallout
                v-if="section !== 'customers'"
                color="info"
                class="mb-4"
            >
                {{ __('tools.testing.snapshots.configure.notice') }}
            </CCallout>

            <!-- Products section -->
            <template v-if="section === 'products'">
                <!-- Group 1: Product Groups -->
                <div class="border rounded mb-3">
                    <div
                        class="d-flex justify-content-between align-items-center p-3 cursor-pointer"
                        style="cursor: pointer"
                        @click="group1Open = !group1Open"
                    >
                        <strong>{{ __('tools.testing.snapshots.configure.products.group_product_groups') }}</strong>
                        <DIcon :name="group1Open ? 'chevron-up' : 'chevron-down'" />
                    </div>
                    <DCollapse :visible="group1Open">
                        <div class="p-3 pt-0">
                            <DFormRow
                                :label="__('tools.testing.snapshots.configure.products.retirement_status')"
                                :hint="__('tools.testing.snapshots.configure.products.retirement_status_hint')"
                            >
                                <div class="d-flex gap-3">
                                    <DFormCheck
                                        id="retirement-none"
                                        type="radio"
                                        :label="__('tools.testing.snapshots.configure.products.retirement_none')"
                                        :checked="localConfig.retirementStatus === 'none'"
                                        @change="selectNoneRetirementStatus"
                                    />
                                    <DFormCheck
                                        id="retirement-all"
                                        type="radio"
                                        :label="__('tools.testing.snapshots.configure.products.retirement_all')"
                                        :checked="localConfig.retirementStatus === null"
                                        @change="localConfig.retirementStatus = null"
                                    />
                                    <DFormCheck
                                        id="retirement-active"
                                        type="radio"
                                        :label="__('tools.testing.snapshots.configure.products.retirement_active')"
                                        :checked="localConfig.retirementStatus === 'active'"
                                        @change="localConfig.retirementStatus = 'active'"
                                    />
                                    <DFormCheck
                                        id="retirement-retired"
                                        type="radio"
                                        :label="__('tools.testing.snapshots.configure.products.retirement_retired')"
                                        :checked="localConfig.retirementStatus === 'retired'"
                                        @change="localConfig.retirementStatus = 'retired'"
                                    />
                                </div>
                            </DFormRow>
                            <DFormRow
                                v-if="productGroups.length > 0"
                                :label="__('tools.testing.snapshots.configure.products.groups')"
                            >
                                <div class="d-flex flex-column gap-1">
                                    <DFormCheck
                                        v-for="group in productGroups"
                                        :id="`pg-${group.id}`"
                                        :key="group.id"
                                        :label="`${group.name} (ID: ${group.id})`"
                                        :value="group.id"
                                        :disabled="localConfig.retirementStatus === 'none'"
                                        :model-value="localConfig.productGroupIds !== null && localConfig.productGroupIds.includes(group.id)"
                                        @change="toggleProductGroup(group.id)"
                                    />
                                </div>
                            </DFormRow>
                        </div>
                    </DCollapse>
                </div>

                <!-- Group 2: Fulfillment Account Groups -->
                <div
                    v-if="fulfillmentAccountGroups.length > 0"
                    class="border rounded mb-3"
                >
                    <div
                        class="d-flex justify-content-between align-items-center p-3"
                        style="cursor: pointer"
                        @click="group2Open = !group2Open"
                    >
                        <strong>{{ __('tools.testing.snapshots.configure.products.group_fulfillment_account_groups') }}</strong>
                        <DIcon :name="group2Open ? 'chevron-up' : 'chevron-down'" />
                    </div>
                    <DCollapse :visible="group2Open">
                        <div class="p-3 pt-0">
                            <DFormRow :label="__('tools.testing.snapshots.configure.products.account_groups')">
                                <div class="d-flex flex-column gap-1">
                                    <DFormCheck
                                        v-for="group in fulfillmentAccountGroups"
                                        :id="`ag-${group.id}`"
                                        :key="group.id"
                                        :label="`${group.name} (ID: ${group.id})`"
                                        :value="group.id"
                                        :model-value="localConfig.fulfillmentAccountGroupIds !== null && localConfig.fulfillmentAccountGroupIds.includes(group.id)"
                                        @change="toggleFulfillmentAccountGroup(group.id)"
                                    />
                                </div>
                            </DFormRow>
                        </div>
                    </DCollapse>
                </div>

                <!-- Group 3: Other Models -->
                <div
                    v-if="productModels.length > 0"
                    class="border rounded mb-3"
                >
                    <div
                        class="d-flex justify-content-between align-items-center p-3"
                        style="cursor: pointer"
                        @click="group3Open = !group3Open"
                    >
                        <strong>{{ __('tools.testing.snapshots.configure.products.group_other_models') }}</strong>
                        <DIcon :name="group3Open ? 'chevron-up' : 'chevron-down'" />
                    </div>
                    <DCollapse :visible="group3Open">
                        <div class="p-3 pt-0">
                            <DFormRow :label="__('tools.testing.snapshots.configure.products.models')">
                                <div class="d-flex flex-column gap-1">
                                    <DFormCheck
                                        v-for="model in productModels"
                                        :id="`pm-${model.class}`"
                                        :key="model.class"
                                        :label="model.name"
                                        :value="model.class"
                                        :model-value="localConfig.productModels !== null && localConfig.productModels.includes(model.class)"
                                        @change="toggleProductModel(model.class)"
                                    />
                                </div>
                            </DFormRow>
                        </div>
                    </DCollapse>
                </div>
            </template>

            <!-- Email templates section -->
            <template v-else-if="section === 'email_templates'">
                <DFormRow
                    v-if="emailLanguages.length > 0"
                    :label="__('tools.testing.snapshots.configure.email_templates.languages')"
                >
                    <div class="d-flex flex-column gap-1">
                        <DFormCheck
                            v-for="language in emailLanguages"
                            :id="`el-${language}`"
                            :key="language"
                            :label="language"
                            :value="language"
                            :model-value="localConfig.emailLanguages !== null && localConfig.emailLanguages.includes(language)"
                            @change="toggleEmailLanguage(language)"
                        />
                    </div>
                </DFormRow>
            </template>

            <!-- SMS templates section -->
            <template v-else-if="section === 'sms_templates'">
                <DFormRow
                    v-if="smsLanguages.length > 0"
                    :label="__('tools.testing.snapshots.configure.sms_templates.languages')"
                >
                    <div class="d-flex flex-column gap-1">
                        <DFormCheck
                            v-for="language in smsLanguages"
                            :id="`sl-${language}`"
                            :key="language"
                            :label="language"
                            :value="language"
                            :model-value="localConfig.smsLanguages !== null && localConfig.smsLanguages.includes(language)"
                            @change="toggleSmsLanguage(language)"
                        />
                    </div>
                </DFormRow>
            </template>

            <!-- Settings section -->
            <template v-else-if="section === 'settings'">
                <DFormRow
                    v-if="settingsModels.length > 0"
                    :label="__('tools.testing.snapshots.configure.settings.models')"
                >
                    <div class="d-flex flex-column gap-1">
                        <DFormCheck
                            v-for="model in settingsModels"
                            :id="`sm-${model.class}`"
                            :key="model.class"
                            :label="model.name"
                            :value="model.class"
                            :model-value="localConfig.settingsModels !== null && localConfig.settingsModels.includes(model.class)"
                            @change="toggleSettingsModel(model.class)"
                        />
                    </div>
                </DFormRow>
            </template>

            <!-- Customers section -->
            <template v-else-if="section === 'customers'">
                <DFormRow
                    class="mt-3"
                    :label="__('tools.testing.snapshots.configure.customers.ids')"
                    :hint="__('tools.testing.snapshots.configure.customers.ids_hint')"
                >
                    <DInput
                        id="customer_ids_input"
                        v-model="customerIdsInput"
                        :placeholder="__('tools.testing.snapshots.configure.customers.ids_placeholder')"
                    />
                </DFormRow>
                <CustomerSectionCounts
                    class="mt-2"
                    :status="status"
                    :counts="counts"
                    :limits="limits"
                />
            </template>
        </div>
    </DModal>
</template>

<script setup>

import { reactive, ref } from 'vue'
import { __ } from '~@/Composables/translation'
import CustomerSectionCounts from '~@/Components/Tools/Testing/Snapshots/CustomerSectionCounts'
import { useSnapshotSectionCounts } from '~@/Composables/snapshotSectionCounts'

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    section: {
        type: String,
        required: true
    },
    currentConfig: {
        type: Object,
        default: () => { return {} }
    },
    productGroups: {
        type: Array,
        default: () => { return [] }
    },
    fulfillmentAccountGroups: {
        type: Array,
        default: () => { return [] }
    },
    emailLanguages: {
        type: Array,
        default: () => { return [] }
    },
    smsLanguages: {
        type: Array,
        default: () => { return [] }
    },
    settingsModels: {
        type: Array,
        default: () => { return [] }
    },
    productModels: {
        type: Array,
        default: () => { return [] }
    },
    limits: {
        type: Object,
        default: () => { return {} }
    },
})

const emit = defineEmits(['close', 'apply'])

const sectionTitles = {
    products: __('tools.testing.snapshots.configure.products.title'),
    email_templates: __('tools.testing.snapshots.configure.email_templates.title'),
    sms_templates: __('tools.testing.snapshots.configure.sms_templates.title'),
    settings: __('tools.testing.snapshots.configure.settings.title'),
    customers: __('tools.testing.snapshots.configure.customers.title'),
}

const sectionTitle = sectionTitles[props.section] ?? props.section

const group1Open = ref(true)
const group2Open = ref(false)
const group3Open = ref(false)

const localConfig = reactive({
    productGroupIds: props.currentConfig.productGroupIds ?? null,
    retirementStatus: props.currentConfig.retirementStatus ?? null,
    fulfillmentAccountGroupIds: props.currentConfig.fulfillmentAccountGroupIds ?? null,
    emailLanguages: props.currentConfig.emailLanguages ?? null,
    smsLanguages: props.currentConfig.smsLanguages ?? null,
    settingsModels: props.currentConfig.settingsModels ?? null,
    customerIds: props.currentConfig.customerIds ?? null,
    productModels: props.currentConfig.productModels ?? null,
})

const customerIdsInput = ref(localConfig.customerIds ? localConfig.customerIds.join(', ') : '')

// Counted from this field's in-progress IDs, so debounced: the field is free text.
const { status, counts } = useSnapshotSectionCounts(
    () => parseCustomerIds(customerIdsInput.value),
    { enabled: () => props.section === 'customers', debounced: true }
)

function selectNoneRetirementStatus () {
    localConfig.retirementStatus = 'none'
    localConfig.productGroupIds = null
}

function toggleProductGroup (id) {
    localConfig.productGroupIds = toggleInArray(localConfig.productGroupIds, id)
}

function toggleFulfillmentAccountGroup (id) {
    localConfig.fulfillmentAccountGroupIds = toggleInArray(localConfig.fulfillmentAccountGroupIds, id)
}

function toggleEmailLanguage (value) {
    localConfig.emailLanguages = toggleInArray(localConfig.emailLanguages, value)
}

function toggleSmsLanguage (value) {
    localConfig.smsLanguages = toggleInArray(localConfig.smsLanguages, value)
}

function toggleSettingsModel (modelClass) {
    localConfig.settingsModels = toggleInArray(localConfig.settingsModels, modelClass)
}

function toggleProductModel (modelClass) {
    localConfig.productModels = toggleInArray(localConfig.productModels, modelClass)
}

function toggleInArray (current, value) {
    const arr = current ? [...current] : []
    const index = arr.indexOf(value)

    if (index === -1) {
        arr.push(value)
    } else {
        arr.splice(index, 1)
    }

    return arr.length > 0 ? arr : null
}

function parseCustomerIds (input) {
    if (!input || !input.trim()) {
        return null
    }

    const ids = input.split(/[\s,]+/)
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n))

    return ids.length > 0 ? ids : null
}

function apply () {
    if (props.section === 'customers') {
        localConfig.customerIds = parseCustomerIds(customerIdsInput.value)
    }

    emit('apply', { ...localConfig })
}

function close () {
    emit('close')
}

</script>

<style lang="scss" scoped>
.modal-content {
    box-shadow: none;
}
</style>
